import { useState, useEffect, useRef } from "react";
import {
  FiX,
  FiGlobe,
  FiCheck,
  FiAlertCircle,
  FiLoader,
  FiExternalLink,
  FiCopy,
  FiInfo,
  FiCheckCircle
} from "react-icons/fi";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import { cuteToast } from "../UI/cute";
import config from "../../config";

// List of inappropriate words for subdomain filtering
const inappropriateWords = [
  'sex', 'porn', 'xxx', 'adult', 'nude', 'naked', 'fuck', 'shit', 'ass',
  'dick', 'cock', 'pussy', 'bitch', 'whore', 'slut', 'rape', 'molest',
  'pedophil', 'incest', 'bestiality', 'escort', 'hooker', 'stripper',
  'onlyfans', 'fetish', 'erotic', 'hentai', 'milf', 'dildo', 'vibrator',
  'orgasm', 'masturbat', 'blowjob', 'handjob', 'cumshot', 'creampie',
  'gangbang', 'threesome', 'swinger', 'cuckold', 'domination', 'bondage'
];

// Check if subdomain contains inappropriate content
const containsInappropriateContent = (subdomain) => {
  const lowerSubdomain = subdomain.toLowerCase();
  return inappropriateWords.some(word => lowerSubdomain.includes(word));
};

// Validate subdomain format
const isValidSubdomainFormat = (subdomain) => {
  // Only lowercase letters, numbers, and hyphens
  // Must start and end with alphanumeric
  // 3-50 characters
  const regex = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;
  return regex.test(subdomain) && !subdomain.includes('--');
};

const PublishModal = ({ show, onClose, project, onPublishSuccess }) => {
  const { sendRequest } = useHttp();
  const [activeTab, setActiveTab] = useState('subdomain'); // 'subdomain' or 'cname'

  // Form fields
  const [siteTitle, setSiteTitle] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [customDomain, setCustomDomain] = useState('');

  // Validation states
  const [subdomainStatus, setSubdomainStatus] = useState({ checking: false, available: null, message: '' });
  const [cnameStatus, setCnameStatus] = useState({ checking: false, valid: null, message: '' });

  // Publishing state
  const [isPublishing, setIsPublishing] = useState(false);

  const checkTimeout = useRef(null);
  const cnameCheckTimeout = useRef(null);

  const baseDomain = config.baseDomain;

  useEffect(() => {
    if (show && project) {
      setSiteTitle(project.name || '');
      const prefilled = project.subdomain || '';
      setSubdomain(prefilled);
      setCustomDomain(project.custom_domain || '');
      setSubdomainStatus({ checking: false, available: null, message: '' });
      setCnameStatus({ checking: false, valid: null, message: '' });

      // Auto-validate pre-filled subdomain so the user sees availability status immediately
      if (prefilled) {
        // Small delay to ensure state is set before validation runs
        setTimeout(() => checkSubdomainAvailability(prefilled), 100);
      }
    }
  }, [show, project]);

  // Check subdomain availability with debounce
  const checkSubdomainAvailability = (value) => {
    // Clear previous timeout
    if (checkTimeout.current) {
      clearTimeout(checkTimeout.current);
    }

    // Basic validation first
    if (!value || value.length < 3) {
      setSubdomainStatus({ checking: false, available: null, message: 'Subdomain must be at least 3 characters' });
      return;
    }

    if (!isValidSubdomainFormat(value)) {
      setSubdomainStatus({
        checking: false,
        available: false,
        message: 'Only lowercase letters, numbers, and hyphens allowed. Must start and end with alphanumeric.'
      });
      return;
    }

    if (containsInappropriateContent(value)) {
      setSubdomainStatus({
        checking: false,
        available: false,
        message: 'This subdomain contains inappropriate content and cannot be used.'
      });
      return;
    }

    // If it's the same as current project subdomain, it's available
    if (project?.subdomain === value) {
      setSubdomainStatus({ checking: false, available: true, message: 'This is your current subdomain' });
      return;
    }

    setSubdomainStatus({ checking: true, available: null, message: 'Checking availability...' });

    // Debounce API call
    checkTimeout.current = setTimeout(() => {
      sendRequest(
        {
          url: `${endPoints.projects}/check-subdomain`,
          method: "POST",
          body: { subdomain: value, project_id: project?.id }
        },
        (response) => {
          if (response.status === 200) {
            setSubdomainStatus({
              checking: false,
              available: response.body.available,
              message: response.body.available ? 'Subdomain is available!' : 'Subdomain is already taken'
            });
          } else {
            setSubdomainStatus({
              checking: false,
              available: false,
              message: response.message || 'Error checking availability'
            });
          }
        }
      );
    }, 500);
  };

  // Handle subdomain change
  const handleSubdomainChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomain(value);
    checkSubdomainAvailability(value);
  };

  // Check CNAME validation
  const checkCnameValidation = (domain) => {
    if (cnameCheckTimeout.current) {
      clearTimeout(cnameCheckTimeout.current);
    }

    if (!domain || domain.length < 4) {
      setCnameStatus({ checking: false, valid: null, message: '' });
      return;
    }

    // Basic domain format validation
    const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
    if (!domainRegex.test(domain)) {
      setCnameStatus({ checking: false, valid: false, message: 'Invalid domain format' });
      return;
    }

    setCnameStatus({ checking: true, valid: null, message: 'Verifying CNAME record...' });

    cnameCheckTimeout.current = setTimeout(() => {
      sendRequest(
        {
          url: `${endPoints.projects}/verify-cname`,
          method: "POST",
          body: { domain, project_id: project?.id }
        },
        (response) => {
          if (response.status === 200) {
            setCnameStatus({
              checking: false,
              valid: response.body.valid,
              message: response.body.valid
                ? 'CNAME record verified successfully!'
                : response.body.message || 'CNAME record not found or incorrect'
            });
          } else {
            setCnameStatus({
              checking: false,
              valid: false,
              message: response.message || 'Error verifying CNAME'
            });
          }
        }
      );
    }, 1000);
  };

  // Handle custom domain change
  const handleCustomDomainChange = (e) => {
    const value = e.target.value.toLowerCase().trim();
    setCustomDomain(value);
    checkCnameValidation(value);
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    cuteToast({
      type: "success",
      title: "Copied",
      message: "Copied to clipboard",
      timer: 1500
    });
  };

  // Handle publish
  const handlePublish = async () => {
    // Validate required fields
    if (!siteTitle.trim()) {
      cuteToast({ type: "error", title: "Error", message: "Site title is required", timer: 3000 });
      return;
    }

    if (!subdomain) {
      cuteToast({ type: "error", title: "Error", message: "Subdomain is required", timer: 3000 });
      return;
    }

    if (subdomainStatus.available === false) {
      cuteToast({ type: "error", title: "Error", message: "Please choose an available subdomain", timer: 3000 });
      return;
    }

    // If custom domain is provided, it should be verified
    if (customDomain && !cnameStatus.valid) {
      cuteToast({ type: "error", title: "Error", message: "Please verify your CNAME record before publishing", timer: 3000 });
      return;
    }

    setIsPublishing(true);

    sendRequest(
      {
        url: `${endPoints.projects}/${project.id}/publish`,
        method: "POST",
        body: {
          site_title: siteTitle,
          subdomain: subdomain,
          custom_domain: customDomain || null
        }
      },
      (response) => {
        setIsPublishing(false);
        if (response.status === 200) {
          cuteToast({
            type: "success",
            title: "Published!",
            message: `Your site is now live at ${subdomain}.${baseDomain}`,
            timer: 4000
          });
          onPublishSuccess();
        } else {
          cuteToast({
            type: "error",
            title: "Error",
            message: response.message || "Failed to publish",
            timer: 3000
          });
        }
      }
    );
  };

  if (!show) return null;

  return (
    <div className="publish-modal-overlay" onClick={onClose}>
      <div className="publish-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="publish-modal-header">
          <h3><FiGlobe /> Publish Your Site</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="publish-modal-content">
          {/* Site Title */}
          <div className="publish-field">
            <label>Site Title</label>
            <input
              type="text"
              value={siteTitle}
              onChange={(e) => setSiteTitle(e.target.value)}
              placeholder="Enter your site title"
              maxLength={100}
            />
            <small>This will be used for SEO and browser tab title</small>
          </div>

          {/* Tabs for Subdomain / Custom Domain */}
          <div className="publish-tabs">
            <button
              className={`publish-tab ${activeTab === 'subdomain' ? 'active' : ''}`}
              onClick={() => setActiveTab('subdomain')}
            >
              Free Subdomain
            </button>
            <button
              className={`publish-tab ${activeTab === 'cname' ? 'active' : ''}`}
              onClick={() => setActiveTab('cname')}
            >
              Custom Domain
            </button>
          </div>

          {/* Subdomain Tab */}
          {activeTab === 'subdomain' && (
            <div className="publish-tab-content">
              <div className="publish-field">
                <label>Subdomain</label>
                <div className="subdomain-input-wrapper">
                  <input
                    type="text"
                    value={subdomain}
                    onChange={handleSubdomainChange}
                    placeholder="your-site-name"
                    maxLength={50}
                    className={subdomainStatus.available === true ? 'valid' : subdomainStatus.available === false ? 'invalid' : ''}
                  />
                  <span className="subdomain-suffix">.{baseDomain}</span>
                  {subdomainStatus.checking && <FiLoader className="status-icon spin" />}
                  {subdomainStatus.available === true && <FiCheckCircle className="status-icon success" />}
                  {subdomainStatus.available === false && <FiAlertCircle className="status-icon error" />}
                </div>
                {subdomainStatus.message && (
                  <small className={subdomainStatus.available === true ? 'success' : subdomainStatus.available === false ? 'error' : ''}>
                    {subdomainStatus.message}
                  </small>
                )}
              </div>

              {/* Preview URL */}
              {subdomain && subdomainStatus.available !== false && (
                <div className="publish-preview">
                  <label>Your site will be available at:</label>
                  <div className="preview-url">
                    <FiGlobe />
                    <span>https://{subdomain}.{baseDomain}</span>
                    <button
                      className="copy-btn"
                      onClick={() => copyToClipboard(`https://${subdomain}.${baseDomain}`)}
                      title="Copy URL"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Custom Domain Tab */}
          {activeTab === 'cname' && (
            <div className="publish-tab-content">
              {/* Instructions */}
              <div className="cname-instructions">
                <div className="instruction-header">
                  <FiInfo /> How to set up your custom domain
                </div>
                <ol>
                  <li>Go to your domain registrar's DNS settings</li>
                  <li>Add a new CNAME record with the following values:</li>
                </ol>
                <div className="cname-values">
                  <div className="cname-row">
                    <span className="cname-label">Type:</span>
                    <span className="cname-value">CNAME</span>
                  </div>
                  <div className="cname-row">
                    <span className="cname-label">Name/Host:</span>
                    <span className="cname-value">
                      www (or @ for root domain)
                      <button
                        className="copy-btn-sm"
                        onClick={() => copyToClipboard('www')}
                      >
                        <FiCopy size={12} />
                      </button>
                    </span>
                  </div>
                  <div className="cname-row">
                    <span className="cname-label">Value/Target:</span>
                    <span className="cname-value">
                      {subdomain || 'your-subdomain'}.{baseDomain}
                      <button
                        className="copy-btn-sm"
                        onClick={() => copyToClipboard(`${subdomain || 'your-subdomain'}.${baseDomain}`)}
                      >
                        <FiCopy size={12} />
                      </button>
                    </span>
                  </div>
                </div>
                <p className="instruction-note">
                  DNS changes may take up to 48 hours to propagate. We'll verify your CNAME record before saving.
                </p>
              </div>

              <div className="publish-field">
                <label>Custom Domain</label>
                <div className="domain-input-wrapper">
                  <input
                    type="text"
                    value={customDomain}
                    onChange={handleCustomDomainChange}
                    placeholder="www.yourdomain.com"
                    className={cnameStatus.valid === true ? 'valid' : cnameStatus.valid === false ? 'invalid' : ''}
                  />
                  {cnameStatus.checking && <FiLoader className="status-icon spin" />}
                  {cnameStatus.valid === true && <FiCheckCircle className="status-icon success" />}
                  {cnameStatus.valid === false && <FiAlertCircle className="status-icon error" />}
                </div>
                {cnameStatus.message && (
                  <small className={cnameStatus.valid === true ? 'success' : cnameStatus.valid === false ? 'error' : ''}>
                    {cnameStatus.message}
                  </small>
                )}
              </div>

              {/* Note about subdomain requirement */}
              {!subdomain && (
                <div className="publish-note warning">
                  <FiAlertCircle />
                  <span>You need to set up a subdomain first before using a custom domain.</span>
                </div>
              )}
            </div>
          )}

          {/* Published Status */}
          {project?.status === 'published' && project?.published_at && (
            <div className="publish-status">
              <FiCheckCircle className="published-icon" />
              <div>
                <strong>Site is Published</strong>
                <span>Last published: {new Date(project.published_at).toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="publish-modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-publish-action"
            onClick={handlePublish}
            disabled={isPublishing || !subdomain || subdomainStatus.available === false || subdomainStatus.checking}
          >
            {isPublishing ? (
              <>
                <FiLoader className="spin" /> Publishing...
              </>
            ) : project?.status === 'published' ? (
              <>
                <FiGlobe /> Update & Republish
              </>
            ) : (
              <>
                <FiGlobe /> Publish Site
              </>
            )}
          </button>
        </div>
      </div>

      <style jsx="true">{`
        .publish-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
          padding: 20px;
        }

        .publish-modal {
          background: #1f2937;
          border-radius: 16px;
          width: 100%;
          max-width: 560px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }

        .publish-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #374151;
        }

        .publish-modal-header h3 {
          margin: 0;
          color: #fff;
          font-size: 20px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .modal-close-btn {
          background: transparent;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 4px;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .modal-close-btn:hover {
          background: #374151;
          color: #fff;
        }

        .publish-modal-content {
          flex: 1;
          padding: 24px;
          overflow-y: auto;
        }

        .publish-field {
          margin-bottom: 20px;
        }

        .publish-field label {
          display: block;
          color: #e5e7eb;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 8px;
        }

        .publish-field input {
          width: 100%;
          padding: 12px 16px;
          background: #374151;
          border: 1px solid #4b5563;
          border-radius: 8px;
          color: #fff;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
        }

        .publish-field input:focus {
          border-color: #7c3aed;
        }

        .publish-field input.valid {
          border-color: #10b981;
        }

        .publish-field input.invalid {
          border-color: #ef4444;
        }

        .publish-field small {
          display: block;
          margin-top: 6px;
          font-size: 12px;
          color: #9ca3af;
        }

        .publish-field small.success {
          color: #10b981;
        }

        .publish-field small.error {
          color: #ef4444;
        }

        .publish-tabs {
          display: flex;
          gap: 0;
          margin-bottom: 20px;
          border-radius: 8px;
          overflow: hidden;
          background: #374151;
        }

        .publish-tab {
          flex: 1;
          padding: 12px 16px;
          background: transparent;
          border: none;
          color: #9ca3af;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .publish-tab:hover {
          color: #fff;
        }

        .publish-tab.active {
          background: #7c3aed;
          color: #fff;
        }

        .publish-tab-content {
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .subdomain-input-wrapper,
        .domain-input-wrapper {
          display: flex;
          align-items: center;
          position: relative;
        }

        .subdomain-input-wrapper input {
          border-radius: 8px 0 0 8px;
          padding-right: 40px;
        }

        .subdomain-suffix {
          padding: 12px 16px;
          background: #4b5563;
          border: 1px solid #4b5563;
          border-left: none;
          border-radius: 0 8px 8px 0;
          color: #9ca3af;
          font-size: 14px;
          white-space: nowrap;
        }

        .domain-input-wrapper input {
          padding-right: 40px;
        }

        .status-icon {
          position: absolute;
          right: 12px;
          font-size: 18px;
        }

        .subdomain-input-wrapper .status-icon {
          right: auto;
          left: calc(100% - 200px);
        }

        .status-icon.success {
          color: #10b981;
        }

        .status-icon.error {
          color: #ef4444;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .publish-preview {
          background: rgba(124, 58, 237, 0.1);
          border: 1px solid rgba(124, 58, 237, 0.3);
          border-radius: 8px;
          padding: 16px;
          margin-top: 16px;
        }

        .publish-preview label {
          display: block;
          color: #9ca3af;
          font-size: 12px;
          margin-bottom: 8px;
        }

        .preview-url {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #7c3aed;
          font-size: 14px;
          font-weight: 500;
        }

        .preview-url span {
          flex: 1;
          word-break: break-all;
        }

        .copy-btn {
          background: transparent;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .copy-btn:hover {
          background: #374151;
          color: #fff;
        }

        .cname-instructions {
          background: #374151;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .instruction-header {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #e5e7eb;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .cname-instructions ol {
          margin: 0 0 12px 0;
          padding-left: 20px;
          color: #d1d5db;
          font-size: 13px;
          line-height: 1.6;
        }

        .cname-values {
          background: #1f2937;
          border-radius: 6px;
          padding: 12px;
        }

        .cname-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 0;
          border-bottom: 1px solid #374151;
        }

        .cname-row:last-child {
          border-bottom: none;
        }

        .cname-label {
          color: #9ca3af;
          font-size: 12px;
          min-width: 100px;
        }

        .cname-value {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #fff;
          font-size: 13px;
          font-family: monospace;
        }

        .copy-btn-sm {
          background: transparent;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 2px;
          border-radius: 2px;
          transition: color 0.2s;
        }

        .copy-btn-sm:hover {
          color: #fff;
        }

        .instruction-note {
          margin: 12px 0 0 0;
          color: #9ca3af;
          font-size: 12px;
          font-style: italic;
        }

        .publish-note {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 13px;
        }

        .publish-note.warning {
          background: rgba(251, 191, 36, 0.1);
          border: 1px solid rgba(251, 191, 36, 0.3);
          color: #fbbf24;
        }

        .publish-status {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 8px;
          margin-top: 16px;
        }

        .published-icon {
          color: #10b981;
          font-size: 24px;
        }

        .publish-status strong {
          display: block;
          color: #10b981;
          font-size: 14px;
          margin-bottom: 2px;
        }

        .publish-status span {
          color: #9ca3af;
          font-size: 12px;
        }

        .publish-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #374151;
          background: #111827;
        }

        .btn-cancel {
          padding: 12px 24px;
          background: transparent;
          border: 1px solid #4b5563;
          border-radius: 8px;
          color: #9ca3af;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #374151;
          color: #fff;
        }

        .btn-publish-action {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 28px;
          background: #7c3aed;
          border: none;
          border-radius: 8px;
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-publish-action:hover:not(:disabled) {
          background: #6d28d9;
        }

        .btn-publish-action:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default PublishModal;
